import React, { useMemo, useState } from "react";
import { usePopSelf } from "@randajan/jet-react/dom/modal";
import { Block } from "@randajan/jet-react/dom/block";
import { Alert, Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import NotificationsOffOutlinedIcon from "@mui/icons-material/NotificationsOffOutlined";
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import PowerSettingsNewOutlinedIcon from "@mui/icons-material/PowerSettingsNewOutlined";

import { mailMeUnsubscribeFormFields, validateMailMeUnsubscribeForm } from "../../../../../arc/forms/unsubscribeForms.js";
import { Say } from "../../../../../arc/locale/consts.js";
import { BlimpMuiFormSection } from "../../../../elements/BlimpMuiFormSection/BlimpMuiFormSection";
import { Locale } from "../../../../assets/Locale.js";
import { useBeforeUnload } from "../../../../assets/hooks/useBeforeUnload.jsx";
import { getProfileErrorId, getValidationFieldError, handleProfileSubmitReply, mergeValidation } from "../profileValidation.js";
import { UnsubscribeCategory } from "./UnsubscribeCategory";
import { getUnsubscribeCategory, unsubscribeActions, unsubscribeDefaultReason, unsubscribeReasons } from "./unsubscribeFormConfig";
import { useUnsubscribeTokenInfo } from "./useUnsubscribeTokenInfo";

import "./UnsubscribeForm.scss";
import { Pane } from "@randajan/jet-react/dom/pane";
import { bifrost } from "../../../../assets/bifrost.js";

const emptyValues = Object.freeze({
    message: "",
    reason: unsubscribeDefaultReason
});

const createPayload = (payloadToken, values, action) => ({
    action,
    message: values.message.trim(),
    payloadToken,
    reason: values.reason
});

export const UnsubscribeForm = (props) => {
    const { payloadToken, onCancel, onSaved } = props;
    const say = Locale.use();
    const [values, setValues] = useState(emptyValues);
    const [touched, setTouched] = useState({});
    const tokenInfo = useUnsubscribeTokenInfo(payloadToken);
    const [submitState, setSubmitState] = useState("");
    const [serverValidation, setServerValidation] = useState(null);
    const [submittingAction, setSubmittingAction] = useState("");
    const unsubscribeCategory = getUnsubscribeCategory(tokenInfo.category);
    const shouldExpandMessage = values.reason !== unsubscribeDefaultReason;
    const isDirty = values.reason !== unsubscribeDefaultReason || !!values.message || !!submittingAction;
    const isTokenReady = tokenInfo.status === "ready";

    useBeforeUnload(isDirty);

    const validationInput = createPayload(payloadToken, values, unsubscribeActions.muteMonth);
    const localValidation = useMemo(() => validateMailMeUnsubscribeForm(validationInput), [payloadToken, values]);
    const validation = useMemo(() => mergeValidation(localValidation, serverValidation), [localValidation, serverValidation]);
    const reasonError = getValidationFieldError(validation, "reason");
    const showReasonError = !!reasonError && (submitState === "validationError" || touched.reason);

    const setReason = event => {
        setValues(current => ({ ...current, reason: event.target.value }));
        setTouched(current => ({ ...current, reason: true }));
        setServerValidation(null);
        setSubmitState("");
    };

    const setMessage = event => {
        setValues(current => ({ ...current, message: event.target.value }));
        setServerValidation(null);
        setSubmitState("");
    };

    const submit = async (event, action = unsubscribeActions.muteMonth) => {
        event.preventDefault();
        if (!isTokenReady) { return; }
        const result = validateMailMeUnsubscribeForm(createPayload(payloadToken, values, action));
        if (!result.isValid) {
            setServerValidation(null);
            setTouched(Object.fromEntries(mailMeUnsubscribeFormFields.map(field => [field, true])));
            setSubmitState("validationError");
            return;
        }
        setSubmittingAction(action);
        setSubmitState("");
        try {
            const reply = await bifrost.tx("/mail/unsubscribe", result.values);
            handleProfileSubmitReply(reply, {
                fields: mailMeUnsubscribeFormFields,
                setIsSubmitting: _ => setSubmittingAction(""),
                setServerValidation,
                setSubmitState,
                setTouched,
                onDone: onSaved
            });
        } catch (err) {
            setSubmitState("submitError");
            setSubmittingAction("");
        }
    };

    return (
        <Block level={0} className="UnsubscribeForm popScreen" caption={say("profileUnsubscribeCaption")}>
            <Box className="BlimpMuiForm" component="form" onSubmit={event => submit(event)} noValidate>
                <Typography className="BlimpMuiForm-intro">{say("profileUnsubscribeIntro")}</Typography>
                {submitState === "validationError" && (
                    <Alert severity="error">{say("formErrorCheckForm")}</Alert>
                )}
                {submitState === "submitError" && (
                    <Alert severity="error">{say("profileUnsubscribeErrorSubmit")}</Alert>
                )}
                {tokenInfo.status === "loading" && (
                    <Alert severity="info">{say("profileUnsubscribeStatusLoading")}</Alert>
                )}
                {tokenInfo.status === "invalid" && (
                    <Alert severity="error">{say("profileUnsubscribeStatusInvalid")}</Alert>
                )}
                {isTokenReady && (
                <Box className="BlimpMuiForm-sections">
                    <UnsubscribeCategory category={unsubscribeCategory} />
                    <BlimpMuiFormSection bodyClassName="UnsubscribeForm-reasons" icon={NotificationsOffOutlinedIcon} title={say("profileUnsubscribeSectionReason")}>
                        <TextField
                            className="BlimpMuiForm-field"
                            error={showReasonError}
                            fullWidth
                            helperText={showReasonError ? say(getProfileErrorId("reason", reasonError)) : " "}
                            label={say("profileUnsubscribeFieldReason")}
                            name="reason"
                            onChange={setReason}
                            required
                            select
                            value={values.reason}
                            variant="outlined"
                        >
                            {unsubscribeReasons.map(reason => (
                                <MenuItem key={reason.value} value={reason.value}>
                                    {say(Say.id("profile", "unsubscribe", "reason", reason.value))}
                                </MenuItem>
                            ))}
                        </TextField>
                    </BlimpMuiFormSection>
                    <Pane position="top" transition={500} expand={shouldExpandMessage}>
                        <BlimpMuiFormSection bodyClassName="UnsubscribeForm-message" icon={MarkEmailReadOutlinedIcon} title={say("profileUnsubscribeSectionMessage")}>
                            <TextField
                                className="BlimpMuiForm-field"
                                fullWidth
                                label={say("profileFieldMessage")}
                                maxRows={9}
                                minRows={2}
                                multiline
                                onChange={setMessage}
                                value={values.message}
                                variant="outlined"
                            />
                        </BlimpMuiFormSection>
                    </Pane>
                </Box>
                )}
                {isTokenReady && (
                    <Box className="UnsubscribeForm-note">
                        <Typography>{say("profileUnsubscribeAuraNote")}</Typography>
                    </Box>
                )}
                {isTokenReady && (
                <Box className="BlimpMuiPanel-actions UnsubscribeForm-actions">
                    <Button
                        className="BlimpMuiPanel-action"
                        disabled={!!submittingAction}
                        onClick={event => submit(event, unsubscribeActions.disableForever)}
                        startIcon={<PowerSettingsNewOutlinedIcon />}
                        type="button"
                        variant="outlined"
                    >
                        {say("profileUnsubscribeActionDisableForever")}
                    </Button>
                    <Button
                        className="BlimpMuiPanel-actionPrimary"
                        disabled={!!submittingAction}
                        startIcon={<PauseCircleOutlineOutlinedIcon />}
                        type="submit"
                        variant="contained"
                    >
                        {say("profileUnsubscribeActionMuteMonth")}
                    </Button>
                </Box>
                )}
            </Box>
        </Block>
    );
};
